import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Avatar,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';

// Animation variants for modals and dialogs
const modalVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -50, transition: { duration: 0.3 } }
};

const UserProfileDialog = ({ open, onClose, user }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="profile-dialog-title"
            sx={{
                '& .MuiDialog-paper': {
                    width: '320px',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    padding: '24px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    position: "absolute",
                    overflow: 'hidden',
                    right: 0,
                    top: 0,
                }
            }}
            BackdropProps={{
                style: { background: 'transparent' },
            }}
        >
            <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                <DialogTitle
                    id="profile-dialog-title"
                    sx={{
                        p: 0,
                        pb: 2, // Padding bottom for separator
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.3)', 
                        mb: 2, // Margin bottom after the title section
                    }}
                >
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: '#388e3c' }}> {/* Darker #2e7d32 title */}
                        User Profile
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        aria-label="close"
                        sx={{ color: '#757575', '&:hover': { color: '#ffffff' } }} // Whiter hover for contrast
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {user ? (
                        <>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    mb: 3,
                                    mx: 'auto',
                                    border: '3px solid rgba(102, 187, 106, 0.5)', 
                                    background: 'rgba(255, 255, 255, 0.2)', 
                                    backdropFilter: 'blur(4px)', 
                                }}
                                src={user.profile_pic || `https://placehold.co/80x80/00796b/ffffff?text=${user.first_name[0]}`}
                                alt={`${user.first_name || 'User'} ${user.last_name || 'Profile'}`}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.1)', // Glassmorphism effect
                                    backdropFilter: 'blur(6px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Typography variant="body2" sx={{ width: '35%', color: '#2e7d32', fontWeight: 'bold' }}>
                                    Name:
                                </Typography>
                                <Typography variant="body1" sx={{ width: '65%', }}> {/* White text for contrast */}
                                    {user.first_name || 'N/A'} {user.last_name || ''}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(6px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Typography variant="body2" sx={{ width: '35%', color: '#2e7d32', fontWeight: 'bold' }}>
                                    Email:
                                </Typography>
                                <Typography variant="body1" sx={{ width: '65%', wordBreak: 'break-all', }}>
                                    {user.email || 'N/A'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(6px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Typography variant="body2" sx={{ width: '35%', color: '#2e7d32', fontWeight: 'bold' }}>
                                    Phone:
                                </Typography>
                                <Typography variant="body1" sx={{ width: '65%', }}>
                                    {user.phone || 'N/A'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(6px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Typography variant="body2" sx={{ width: '35%', color: '#2e7d32', fontWeight: 'bold' }}>
                                    Country:
                                </Typography>
                                <Typography variant="body1" sx={{ width: '65%', }}>
                                    {user.country || 'N/A'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(6px)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                <Typography variant="body2" sx={{ width: '35%', color: '#2e7d32', fontWeight: 'bold' }}>
                                    Language:
                                </Typography>
                                <Typography variant="body1" sx={{ width: '65%', }}>
                                    {user.language || 'N/A'}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Typography variant="body1" color="#ffffff">Loading user data...</Typography>
                    )}
                </DialogContent>
            </motion.div>
        </Dialog>
    );
};

export default UserProfileDialog;